import React, { forwardRef, Component, Suspense } from 'reactn';
import Datatable, { MTableToolbar } from 'material-table';
import {
  Grid,
  withStyles,
  Typography,
  Box,
  Button,
  IconButton,
  Tooltip,
  Paper,
  Chip
} from '@material-ui/core';
import styles from './index.styles';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import RefreshIcon from '@material-ui/icons/Refresh';
import FilterListIcon from '@material-ui/icons/FilterList';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import SortIcon from '@material-ui/icons/Sort';
import { Loader, CustomTheme } from '@orijinworks/frontend-commons';
import {
  getStaffList,
  createStaff,
  updateStaff,
  deactivateStaff,
  activateStaff,
  getStates,
  getLocationFeatureStatus,
  whiteListStaff,
  exportUserAuditHistory
} from '../../../util/APIUtils';
import {
  setStaffDetails,
  STAFF_DETAILS,
  ROLES,
  SUPER_ADMIN_ROLES,
  LOCATIONS,
  HOUSING_UNITS
} from '../../../constants';
import { ManagementService } from '../management.service';
import moment from 'moment';
import Hoverable from '../../hoverable';
import { theme } from '../../../v2/core/containers/theme';
import { isPermitted, PermissionControl } from '../../permission-control/permission-control';
import { UserIcon } from '../../UserIcon';
import { withUnleash } from '../../../v2/core/unleash';
import { UnleashService } from '../../../v2/services';
import StaffRowActionsMenu from './StaffRowActionsMenu';
import { downloadBlob } from '../../../v2/Form/utils/csv-export.util';
const ManageStaffContainer = React.lazy(() => import('./manage-staff-container/'));
const ConfirmationModal = React.lazy(() => import('../../confirmation-modal'));
const ResponseBox = React.lazy(() => import('../../response-box'));
const CreateStaff = React.lazy(() => import('../../../v2/modules/create-staff'));

class Staff extends Component {
  managementService = new ManagementService();
  tableRef = React.createRef();
  state = {
    showEmptyDataSourceMessage: false,
    response: {
      isOpen: false,
      data: null
    },
    staff: {
      data: []
    },
    manageStaff: {
      isOpen: false,
      isEdit: false,
      isConfirmationOpen: false,
      data: null,
      statesCollection: [],
      description: null,
      cbacConfigFlag: false,
      permissions: [],
      isPermissionAllowed: false
    },
    location: null,
    staffProfile: {
      isOpen: false,
      staffData: {}
    },
    isAutoCreateAvailable: false,
    isAttorneyAllowed: false,
    permissionLookup: {}
  };

  getStaffListColumns = () => {
    const { classes } = this.props;
    const permissionLookup = this.state.permissionLookup || {};

    return [
      {
        title: 'Username',
        field: 'userName',
        render: rowData => {
          return (
            <Hoverable title={rowData.userName}>
              <Button
                onClick={() => this.onRedirect(rowData)}
                classes={{
                  label: classes.btnNameLabel
                }}
                data-cy-name={`form-name-${rowData.formId}`}
                className={classes.formName}
                color="primary"
              >
                <Box display="flex" alignItems="center" width="100%">
                  {this.getIcon(rowData)} &nbsp;
                  {rowData.userName}
                </Box>
              </Button>
            </Hoverable>
          );
        }
      },
      {
        title: 'First Name',
        field: 'firstName',
        render: rowData => <Hoverable title={rowData.firstName}>{rowData.firstName}</Hoverable>
      },
      {
        title: 'Last Name',
        field: 'lastName',
        render: rowData => <Hoverable title={rowData.lastName}>{rowData.lastName}</Hoverable>
      },
      {
        title: 'Role',
        field: 'role',
        render: rowData => <Hoverable title={rowData.role}>{rowData.role}</Hoverable>
      },
      {
        title: 'Created Date',
        field: 'createdDate',
        render: rowData => (
          <Hoverable title={moment(rowData.createdDate).format('MM-DD-YYYY')}>
            {moment(rowData.createdDate).format('MM-DD-YYYY')}
          </Hoverable>
        ),
        cellStyle: {
          minWidth: 150
        }
      },
      {
        title: 'Status',
        field: 'status',
        cellStyle: {
          width: 100,
          maxWidth: 100,
          overflow: 'auto'
        },
        defaultFilter: ['true'],
        render: rowData => (
          <Hoverable title={rowData.status ? 'active' : 'inactive'}>
            <p style={{ color: rowData.status ? 'black' : theme.palette.text.red }}>{rowData.status ? 'active' : 'inactive'}</p>
          </Hoverable>
        ),
        lookup: { true: 'active', false: 'inactive' }
      },
      {
        title: 'Permissions',
        field: 'enabledPermissions',
        export: false,
        lookup: permissionLookup,
        customFilterAndSearch: (filterValue, rowData) => {
          if (this.isElevatedAdminStaffRow(rowData)) {
            return (
              filterValue == null || (Array.isArray(filterValue) && filterValue.length === 0)
            );
          }
          if (filterValue == null || (Array.isArray(filterValue) && filterValue.length === 0)) {
            return true;
          }
          const selected = Array.isArray(filterValue) ? filterValue : [filterValue];
          const perms = rowData.enabledPermissions || [];
          const ids = new Set(perms.map(p => (p && p.permissionId != null ? String(p.permissionId) : null)).filter(Boolean));
          return selected.some(sel => ids.has(String(sel)));
        },
        cellStyle: {
          minWidth: 220,
          verticalAlign: 'top'
        },
        render: rowData => {
          if (this.isElevatedAdminStaffRow(rowData)) {
            return (
              <Typography variant="body2" className={classes.notApplicableText} component="span">
                Not Applicable
              </Typography>
            );
          }
          const perms = rowData.enabledPermissions || [];
          if (!perms.length) {
            return (
              <Typography variant="body2" className={classes.notApplicableText} component="span">
                Not applicable
              </Typography>
            );
          }
          return (
            <Box className={classes.permissionChipsWrap}>
              {perms.map(p => (
                <Chip
                  key={p.permissionId != null ? String(p.permissionId) : p.permissionName}
                  size="small"
                  label={p.permissionDisplayName || p.permissionName || ''}
                />
              ))}
            </Box>
          );
        }
      },
      {
        title: 'Actions',
        field: 'personId',
        sorting: false,
        filtering: false,
        export: false,
        cellStyle: { whiteSpace: 'nowrap', width: 56, textAlign: 'center' },
        render: rowData => (
          <StaffRowActionsMenu rowData={rowData} onViewProfile={this.onRedirect} onExportUserHistory={this.onExportUserHistory}  />
        )
      }
    ];
  };

  /** Super Admin / System Admin — staff permission chips do not apply at facility scope. */
  isElevatedAdminStaffRow = rowData => {
    const label = (rowData.role || '').trim().toLowerCase();
    if (!label) {
      return false;
    }
    if (label === ROLES.SYSTEM_ADMIN.trim().toLowerCase()) {
      return true;
    }
    return SUPER_ADMIN_ROLES.some(roleName => roleName.trim().toLowerCase() === label);
  };

  /**
   * @name getStates
   * @desc Fetches states from hard-coded JSON file.
   * @return {void}
   */
  getStates = () => {
    if (this.state.manageStaff.statesCollection.length > 0) {
      return;
    }
    getStates().then(
      _successLog => {
        this.setState({
          manageStaff: {
            ...this.state.manageStaff,
            statesCollection: _successLog
          }
        });
      },
      _errorLog => {}
    );
  };

  onRedirect = rowData => {
    this.setGlobal({
      staffProfileID: rowData.personId,
      staffSelectedLocation: this.state.location.active,
      isStaffProfileOpen: false,
      staffRole: rowData.role
    });
    this.props.history.push(`/users/${rowData.personId}?type=staff`);
  };

  onExportUserHistory = async rowData => {
    const personId = rowData?.personId;
    if (!personId) {
      return;
    }
    try {
      const { blob } = await exportUserAuditHistory(personId);
      if (!(blob instanceof Blob)) {
        console.error('Export user history did not return a file.');
        return;
      }
      
      downloadBlob(`user-audit-history-${rowData.personId}.xlsx`,blob);
      } catch (error) {
        console.error('Failed to export user history.', error);
      }
  };

  /**
   * @name checkFeatureStatus
   * @param {array} features
   * @param {string} featureName
   * @param {string} dataType Data type of returning value
   * @param {string} keyCheckName Name of key against which that needs to be checked.
   * @desc Returns feature status case
   * @return {boolean} status
   */
  checkFeatureStatus = (features, featureName, dataType, keyCheckName) => {
    const feature = features.find(feature => feature[keyCheckName] === featureName);
    switch (dataType) {
      case 'boolean':
        if (feature) {
          return feature.status;
        }
        return false;
      case 'object':
        return feature || { [keyCheckName]: featureName };
      default:
        return null;
    }
  };

  /**
   * @name selectData
   * @param {string} type
   * @desc A method used to select datatable data based upon type.
   * @return {void}
   */
  selectData = type => {
    const collection = this.tableRef.dataManager.filteredData;
    let data = [];
    collection.map(_user => {
      _user.tableData.checked = false;
      return _user;
    });
    if (type === 'page') {
      data = this.tableRef.dataManager.pagedData;
    } else {
      data = this.tableRef.dataManager.filteredData;
    }
    
    data.forEach(user => {
      const userFindIndex = collection.findIndex(_users => _users.personId === user.personId);
      collection[userFindIndex].tableData.checked = true;
    });
    this.setState({
      staff: {
        ...this.state.staff,
        data: collection
      }
    });
  };

  /**
   * @name getFeatures
   * @param {number} locationId
   * @desc Fetches feature w.r.t to location.
   * @return {void}
   */
  getFeatures = async locationId => {
    this.setGlobal({
      isLoading: true
    });
    try {
      const isAttorneyAllowed = await getLocationFeatureStatus(locationId, this.managementService.MessagingAttorney);
      const isAutoCreateAvailable = await getLocationFeatureStatus(
        locationId,
        this.managementService.AutoCreateRelationships
      );

      this.setGlobal({
        isLoading: false
      });
      this.setState({
        isAttorneyAllowed: isAttorneyAllowed.status,
        isAutoCreateAvailable: isAutoCreateAvailable.status
      });
    } catch (e) {
      console.error(e);
      this.setGlobal({
        isLoading: false
      });
    }
  };

  /**
   * @name staffProfileDialogHandler
   * @param {boolean} isOpen
   * @param {object} staffData (Required only when isOpen = true) sets staff data into state.
   * @desc Opens / close staff profile dialog. Sets data as well.
   * @return {void}
   */
  staffProfileDialogHandler = (isOpen, staffData = {}) => {
    this.setState({
      staffProfile: {
        ...this.state.staffProfile,
        isOpen,
        staffData
      }
    });
  };

  /**
   * @name getFeatureData
   */

  /**
   * @name getSortedLocations
   * @desc Get sorted locations based on locationType (same logic as manage-staff-v2, but only facilities)
   * @return {Array} Sorted locations array
   */
  getSortedLocations = () => {
    if (this.state.location && this.state.location.type === 'hu') {
      return HOUSING_UNITS.sort(function (a, b) {
        let keyA = a.locationName;
        let keyB = b.locationName;
        if (keyA < keyB) return -1;
        if (keyA > keyB) return 1;
        return 0;
      });
    } else {
      // Filter to show only facilities (locationTypeId === 3)
      return LOCATIONS
        .filter((l) => { return l.locationTypeId === 3 })
        .sort(function (a, b) {
          var keyA = a.locationName,
            keyB = b.locationName;
          if (keyA < keyB) return -1;
          if (keyA > keyB) return 1;
          return 0;
        });
    }
  };

  /**
   * @name getJurisdictionName
   * @desc Get jurisdiction name from LOCATIONS
   * @return {string} Jurisdiction name
   */
  getJurisdictionName = () => {
    const jurisdiction = LOCATIONS.find(
      (l) => l.locationTypeId === parseInt(process.env.REACT_APP_APDS_JURISDICTION_ID)
    );
    return jurisdiction ? jurisdiction.locationName : '';
  };

  /**
   * @name initPropToState
   * @desc A wrapper method to initialize properties from prop to state.
   * @return {void}
   */
  initPropToState = () => {
    this.setState({
      location: this.props.location
    });
    this.getStaffList(this.props.location.active);
  };

  componentDidMount() {
    this.initPropToState();

    this.setGlobal({
      refreshStaffList: opts => this.getStaffList(this.props.location.active, opts)
    });
  }

  componentWillUnmount() {
    this.setGlobal({
      refreshStaffList: null
    });
  }

  componentDidUpdate() {
    if (this.props.location.active !== this.state.location.active) {
      this.initPropToState();
      this.forceUpdate();
    }
  }

  handleConfirmationDialog = (isConfirmationOpen, data, description = '') => {
    const manageStaff = {
      ...this.state.manageStaff,
      description,
      isConfirmationOpen
    };
    if (isConfirmationOpen) {
      manageStaff['data'] = data;
    } else {
      manageStaff['data'] = null;
    }
    this.setState({
      manageStaff
    });
  };

  /**
   * @name getStaffList
   * @param {number} locationId
   * @param {{ quiet?: boolean }} [options] quiet: refresh list without global isLoading (e.g. profile facilities tab).
   * @desc Fetches staff list w.r.t location.
   * @return {void}
   */
  getStaffList = (locationId, options = {}) => {
    const quiet = options.quiet === true;
    if (!quiet) {
      this.setGlobal({
        isLoading: true
      });
    }
    getStaffList(locationId, false).then(_successLog => {
      const data = _successLog.staffList.map(staff => {
        const activeData = staff.rolesLocation.find(
          _roleLocation => _roleLocation.locationId === this.props.location.active
        );
        if (activeData) {
          staff['status'] = activeData.active;
          staff['role'] = activeData.displayName;
        } else {
          staff['role'] = 'N/A';
          staff['status'] = false;
        }
        return staff;
      });

      const rawPermissionLookup = {};
      data.forEach(staff => {
        (staff.enabledPermissions || []).forEach(p => {
          if (p && p.permissionId != null) {
            const key = String(p.permissionId);
            if (!rawPermissionLookup[key]) {
              rawPermissionLookup[key] = p.permissionDisplayName || p.permissionName || key;
            }
          }
        });
      });
      const permissionLookup = {};
      Object.keys(rawPermissionLookup)
        .sort((a, b) => (rawPermissionLookup[a] || '').localeCompare(rawPermissionLookup[b] || '', undefined, { sensitivity: 'base' }))
        .forEach(k => {
          permissionLookup[k] = rawPermissionLookup[k];
        });

      this.setState(
        {
          staff: {
            data
          },
          permissionLookup,
          showEmptyDataSourceMessage: true
        },
        () => {
          if (!quiet) {
            this.setGlobal({
              isLoading: false
            });
          }
        }
      );
    });
  };

  /**
   * @name clearRawData
   * @param {object} payload
   * @desc Remove's FE items added in payload in order to avoid any un-expected error from BE.
   * @return {object} refinedPayload
   */
  clearRawData = payload => {
    payload.rolesAtLocation = payload.rolesAtLocation.map(role => {
      if (role.hasOwnProperty('isNew')) {
        delete role.isNew;
      }
      return role;
    });
    return payload;
  };

  /**
   * @name whiteListStaff
   * @param {object} staffData
   * @desc White list's staff data if Auto_Create_Relationships is true.
   * @return {void}
   */
  whiteListStaff = async staffData => {
    if (this.state.isAutoCreateAvailable) {
      try {
        const requestPayload = {
          staffPersonId: staffData.personId,
          locations: staffData.locations.map(location => ({
            locationId: location.locationId,
            status: location.active
          }))
        };
        await whiteListStaff(requestPayload);
      } catch (e) {
        console.error(e);
      }
    }
  };

  getIcon = rowData => {
    const roleType = rowData && rowData.roleTypes ? rowData.roleTypes.split(',') : '';
    if (this.global.cbacConfigFlag) {
      const allowedRoles = [ROLES.SUPER_ADMIN, ROLES.TEACHER, ROLES.STAFF, ROLES.MESSAGING_ONLY];
      if (allowedRoles.some((role) => roleType.includes(role))) {
        return <VerifiedUserIcon />;
      } else {
        return (
          <svg
            xmlns="https://www.w3.org/2000/svg"
            xlink="https://www.w3.org/1999/xlink"
            svgjs="https://svgjs.com/svgjs"
            version="1.1"
            width="25px"
            height="25px"
            x="0"
            y="0"
            viewBox="0 0 480.004 480.004"
            space="preserve"
            class=""
          >
            <g>
              <g xmlns="http://www.w3.org/2000/svg">
                <g>
                  <path
                    d="M479.202,292.692l0.072-0.024l-78.808-171.952l19.112-9.512l-7.152-14.32l-18.632,9.28l-2.52-5.496    c-1.842-4.016-6.59-5.779-10.606-3.938c-1.742,0.799-3.139,2.196-3.938,3.938l-2.248,4.912c-13.56-6.298-28.329-9.566-43.28-9.576    h-67.2v-16h16v-16H266.25l5.752-23.016c4.416-17.673-6.33-35.58-24.003-39.997c-17.673-4.416-35.58,6.33-39.997,24.003    c-1.312,5.25-1.312,10.743,0,15.993l5.752,23.016h-13.752v16h16v16h-67.16c-14.966,0.012-29.75,3.288-43.32,9.6l-2.248-4.912    c-1.841-4.016-6.59-5.779-10.606-3.938c-1.742,0.799-3.139,2.196-3.938,3.938l-2.52,5.496l-18.632-9.32l-7.152,14.336    l19.112,9.552L0.73,292.668c-0.486,1.045-0.734,2.184-0.728,3.336c0.035,30.913,25.087,55.965,56,56h80    c30.913-0.035,55.965-25.087,56-56c-0.02-1.149-0.293-2.28-0.8-3.312l-79.024-172.6c11.487-5.332,24-8.092,36.664-8.088h67.16    v292.688l-27.312,27.312h-28.688c-22.08,0.026-39.974,17.92-40,40c0,4.418,3.582,8,8,8h224c4.418,0,8-3.582,8-8    c-0.026-22.08-17.92-39.974-40-40h-28.688l-27.312-27.312V112.004h67.2c12.664-0.004,25.177,2.756,36.664,8.088L288.73,292.668    c-0.486,1.045-0.734,2.184-0.728,3.336c0.035,30.913,25.087,55.965,56,56h80c30.913-0.035,55.965-25.087,56-56    C479.983,294.855,479.71,293.724,479.202,292.692z M136.002,336.004h-80c-19.002-0.021-35.375-13.387-39.2-32h158.4    C171.378,322.617,155.004,335.983,136.002,336.004z M171.538,288.004H20.466l73.488-160.328c1.342,0.36,2.754,0.36,4.096,0    L171.538,288.004z M226.618,22.532c6.23-7.392,17.273-8.333,24.665-2.103c0.759,0.64,1.463,1.344,2.103,2.103    c3.236,4.116,4.379,9.5,3.096,14.576l-6.728,26.896H230.25l-6.728-26.896C222.239,32.032,223.383,26.648,226.618,22.532z     M232.002,96.004v-16h16v16H232.002z M248.002,112.004v288h-16v-288H248.002z M320.002,448.004    c10.168,0.012,19.229,6.418,22.632,16H137.37c3.403-9.582,12.464-15.988,22.632-16H320.002z M268.69,432.004h-57.376l16-16h25.376    L268.69,432.004z M381.938,127.7c1.321,0.465,2.77,0.405,4.048-0.168l73.552,160.472H308.466L381.938,127.7z M424.002,336.004h-80    c-19.002-0.021-35.375-13.387-39.2-32h158.4C459.378,322.617,443.004,335.983,424.002,336.004z"
                    fill={CustomTheme.palette.primary.main}
                    data-original="#000000"
                  />
                </g>
              </g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
            </g>
          </svg>
        );
      }
    } else {
      if (rowData.role === ROLES.ATTORNEY) {
        return (
          <svg
            xmlns="https://www.w3.org/2000/svg"
            xlink="https://www.w3.org/1999/xlink"
            svgjs="https://svgjs.com/svgjs"
            version="1.1"
            width="25px"
            height="25px"
            x="0"
            y="0"
            viewBox="0 0 480.004 480.004"
            space="preserve"
            class=""
          >
            <g>
              <g xmlns="https://www.w3.org/2000/svg">
                <g>
                  <path
                    d="M479.202,292.692l0.072-0.024l-78.808-171.952l19.112-9.512l-7.152-14.32l-18.632,9.28l-2.52-5.496    c-1.842-4.016-6.59-5.779-10.606-3.938c-1.742,0.799-3.139,2.196-3.938,3.938l-2.248,4.912c-13.56-6.298-28.329-9.566-43.28-9.576    h-67.2v-16h16v-16H266.25l5.752-23.016c4.416-17.673-6.33-35.58-24.003-39.997c-17.673-4.416-35.58,6.33-39.997,24.003    c-1.312,5.25-1.312,10.743,0,15.993l5.752,23.016h-13.752v16h16v16h-67.16c-14.966,0.012-29.75,3.288-43.32,9.6l-2.248-4.912    c-1.841-4.016-6.59-5.779-10.606-3.938c-1.742,0.799-3.139,2.196-3.938,3.938l-2.52,5.496l-18.632-9.32l-7.152,14.336    l19.112,9.552L0.73,292.668c-0.486,1.045-0.734,2.184-0.728,3.336c0.035,30.913,25.087,55.965,56,56h80    c30.913-0.035,55.965-25.087,56-56c-0.02-1.149-0.293-2.28-0.8-3.312l-79.024-172.6c11.487-5.332,24-8.092,36.664-8.088h67.16    v292.688l-27.312,27.312h-28.688c-22.08,0.026-39.974,17.92-40,40c0,4.418,3.582,8,8,8h224c4.418,0,8-3.582,8-8    c-0.026-22.08-17.92-39.974-40-40h-28.688l-27.312-27.312V112.004h67.2c12.664-0.004,25.177,2.756,36.664,8.088L288.73,292.668    c-0.486,1.045-0.734,2.184-0.728,3.336c0.035,30.913,25.087,55.965,56,56h80c30.913-0.035,55.965-25.087,56-56    C479.983,294.855,479.71,293.724,479.202,292.692z M136.002,336.004h-80c-19.002-0.021-35.375-13.387-39.2-32h158.4    C171.378,322.617,155.004,335.983,136.002,336.004z M171.538,288.004H20.466l73.488-160.328c1.342,0.36,2.754,0.36,4.096,0    L171.538,288.004z M226.618,22.532c6.23-7.392,17.273-8.333,24.665-2.103c0.759,0.64,1.463,1.344,2.103,2.103    c3.236,4.116,4.379,9.5,3.096,14.576l-6.728,26.896H230.25l-6.728-26.896C222.239,32.032,223.383,26.648,226.618,22.532z     M232.002,96.004v-16h16v16H232.002z M248.002,112.004v288h-16v-288H248.002z M320.002,448.004    c10.168,0.012,19.229,6.418,22.632,16H137.37c3.403-9.582,12.464-15.988,22.632-16H320.002z M268.69,432.004h-57.376l16-16h25.376    L268.69,432.004z M381.938,127.7c1.321,0.465,2.77,0.405,4.048-0.168l73.552,160.472H308.466L381.938,127.7z M424.002,336.004h-80    c-19.002-0.021-35.375-13.387-39.2-32h158.4C459.378,322.617,443.004,335.983,424.002,336.004z"
                    fill={CustomTheme.palette.primary.main}
                    data-original="#000000"
                  />
                </g>
              </g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
              <g xmlns="http://www.w3.org/2000/svg"></g>
            </g>
          </svg>
        );
      } else {
        return <VerifiedUserIcon />;
      }
    }
  };
  /**
   * @name removeDuplicates
   * @param {array} collection
   * @desc Remove's duplicate rows from payload.
   * @return {void}
   */
  removeDuplicates = collection => {
    return collection;
  };

  /**
   * @name manageStaffForm
   * @param {object} data
   * @desc Manange's staff form, method decides whether its create / edit and
   * initiates respective call.
   * @return {void}
   */
  manageStaffForm = data => {
    this.setGlobal({
      isLoading: true
    });
    const payload = [this.clearRawData(data)];
    let request = null;
    if (this.state.manageStaff.isEdit) {
      request = updateStaff(payload);
    } else {
      request = createStaff(payload);
    }
    request.then(
      async _successLog => {
        this.setGlobal({
          isLoading: false
        });
        this.getStaffList(this.props.location.active);

        const responseData = [];
        _successLog.successList.forEach(_successNodes => {
          responseData.push({
            statusCode: _successNodes.statusCode,
            text: `${_successNodes.email} ${this.state.manageStaff.isEdit ? 'updated' : 'created'} successfully.`
          });
        });
        _successLog.errorList.forEach(_errorNodes => {
          responseData.push({
            statusCode: _errorNodes.statusCode,
            text: _errorNodes.statusMessage
          });
        });

        this.setState({
          manageStaff: {
            ...this.state.manageStaff,
            isOpen: false,
            isConfirmationOpen: false,
            data: null
          },
          response: {
            isOpen: true,
            data: responseData
          }
        });
        if (_successLog.successList.length > 0) {
          this.whiteListStaff({
            personId: _successLog.successList[0].personKey,
            locations: payload[0].rolesAtLocation
          });
        } else {
          console.error('White listing staff skipped due to no success data.');
        }
        setStaffDetails({});
      },
      _errorLog => {
        setStaffDetails({});
        React.setGlobal({
          isLoading: false
        });
        console.error('Something went wrong while creating user, please check network call.');
      }
    );
  };

  /**
   * @name changeStaffStatus
   * @param {boolean} updatedStaffStatus
   * @desc Updates staff status with respect to param. passed.
   * @return {void}
   */
  changeStaffStatus = updatedStaffStatus => {
    let requestHandler = null;
    let whiteListLocationCollection = [];
    if (updatedStaffStatus) {
      const { staffData } = this.state.staffProfile;
      requestHandler = activateStaff({
        reactivateRequest: [
          {
            personId: staffData.personId,
            rolesAtLocation: staffData.rolesLocation
              .filter(role => role.locationId === this.props.location.active)
              .map(filteredLocation => {
                return {
                  locationKey: filteredLocation.locationId,
                  roleName: filteredLocation.roleName
                };
              })
          }
        ]
      });

      const activeLocationIndex = STAFF_DETAILS.locations.findIndex(role => role.locationId === this.props.location.active);
      STAFF_DETAILS.locations[activeLocationIndex].active = true;
      whiteListLocationCollection = STAFF_DETAILS.locations;
    } else {
      requestHandler = deactivateStaff({
        personKeys: [this.state.staffProfile.staffData.personId]
      });
      whiteListLocationCollection = STAFF_DETAILS.locations.map(location => {
        location['active'] = false;
        return location;
      });
    }
    this.setGlobal({
      isLoading: true
    });
    requestHandler.then(
      _successLog => {
        this.setGlobal({
          isLoading: false
        });

        this.getStaffList(this.props.location.active);

        const responseData = [];
        _successLog.successList.forEach(_successNodes => {
          responseData.push({
            statusCode: _successNodes.statusCode,
            text: `${_successNodes.email} status updated successfully.`
          });
        });
        _successLog.errorList.forEach(_errorNodes => {
          responseData.push({
            statusCode: _errorNodes.statusCode,
            text: _errorNodes.statusMessage
          });
        });
        this.whiteListStaff({
          personId: this.state.staffProfile.staffData.personId,
          locations: whiteListLocationCollection
        });
        this.setState({
          staffProfile: {
            isOpen: false,
            staffData: {}
          },
          response: {
            isOpen: true,
            data: responseData
          }
        });
        setStaffDetails({});
      },
      _errorLog => {
        console.error('Something went wrong while setting status.');
        setStaffDetails({});
        this.setGlobal({
          isLoading: false
        });
        this.setState({
          staffProfile: {
            isOpen: false,
            staffData: {}
          }
        });
      }
    );
  };

  /**
   * @name handleResponseDialog
   * @param {boolean} isOpen
   * @desc Response dialog toggle handling.
   * @return {void}
   */
  handleResponseDialog = isOpen => {
    this.setState({
      response: {
        ...this.state.response,
        isOpen
      }
    });
  };

  handleManageDialog = isOpen => {
    let tempManageData = {};
    if (isOpen) {
      this.getFeatures(this.props.location.active);
      tempManageData = {
        isEdit: false,
        data: null
      };
    }
    this.setState({
      manageStaff: {
        ...this.state.manageStaff,
        ...tempManageData,
        isOpen
      }
    });
  };

  /**
   * @name handleCreateStaffFromForm
   * @param {object} formValues - Form values from CreateStaff component
   * @desc Transforms form values into API payload format and calls createStaff API
   * @return {void}
   */
  handleCreateStaffFromForm = (formValues) => {
    // Check if Super Admin role is selected
    const isSuperAdminSelected = formValues.selectedRole?.roleName === ROLES.SUPER_ADMIN || 
                                  formValues.selectedRole?.displayName === ROLES.SUPER_ADMIN;

    // Validate required fields
    // For Super Admin, locations are not required
    if (!formValues.selectedRole) {
      const responseData = [{
        statusCode: 400,
        text: 'Please select a role.'
      }];
      this.setState({
        response: {
          isOpen: true,
          data: responseData
        }
      });
      return;
    }

    // For non-Super Admin roles, locations are required
    if (!isSuperAdminSelected && (!formValues.selectedLocations || formValues.selectedLocations.length === 0)) {
      const responseData = [{
        statusCode: 400,
        text: 'Please select at least one location.'
      }];
      this.setState({
        response: {
          isOpen: true,
          data: responseData
        }
      });
      return;
    }

    // Transform formValues into the payload format expected by createStaff API
    // Note: Role class expects: locationId (Integer), roleName (String), and optionally roleId (Integer) and startDate (String)
    // When CBAC is active, roleId is REQUIRED (not optional) - the API will throw NullPointerException if roleId is null
    // The 'active' field is NOT part of the API Role class, so we don't include it
    // For Super Admin, rolesAtLocation should be empty array
    const rolesAtLocation = isSuperAdminSelected 
      ? [] 
      : formValues.selectedLocations.map((location) => {
          const rolePayload = {
            locationId: location.locationId || location.id,
            roleName: formValues.selectedRole.roleName || formValues.selectedRole.displayName || '',
          };
          
          // Include roleId if available (required when CBAC is active)
          // Super Admin uses roleId: -1, which we don't want to include for regular roles
          // Always include roleId when it exists and is a valid number (not -1 for Super Admin)
          if (formValues.selectedRole.roleId != null && 
              formValues.selectedRole.roleId !== undefined && 
              formValues.selectedRole.roleId !== -1) {
            rolePayload.roleId = formValues.selectedRole.roleId;
          }
          
          return rolePayload;
        });

    const payload = {
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      displayFirstName: formValues.displayFirstName || formValues.firstName,
      displayLastName: formValues.displayLastName || formValues.lastName,
      email: formValues.email,
      password: formValues.password,
      rolesAtLocation: rolesAtLocation,
      shouldCreateSuperAdmin: isSuperAdminSelected,
    };

    if (formValues.staffPermissionToggles && formValues.staffPermissionToggles.length > 0) {
      payload.staffPermissionToggles = formValues.staffPermissionToggles;
    }

    // Clean the payload (remove any frontend-only properties)
    const cleanedPayload = this.clearRawData(payload);

    this.setGlobal({
      isLoading: true
    });

    createStaff([cleanedPayload]).then(
      async _successLog => {
        this.setGlobal({
          isLoading: false
        });
        this.getStaffList(this.props.location.active);

        const responseData = [];
        _successLog.successList.forEach(_successNodes => {
          responseData.push({
            statusCode: _successNodes.statusCode,
            text: `${_successNodes.email} created successfully.`
          });
        });
        _successLog.errorList.forEach(_errorNodes => {
          responseData.push({
            statusCode: _errorNodes.statusCode,
            text: _errorNodes.statusMessage
          });
        });

        this.setState({
          manageStaff: {
            ...this.state.manageStaff,
            isOpen: false,
            isConfirmationOpen: false,
            data: null
          },
          response: {
            isOpen: true,
            data: responseData
          }
        });

        // White list staff if Auto_Create_Relationships is enabled and creation was successful
        // Skip white listing for Super Admin (no locations assigned)
        if (_successLog.successList.length > 0 && !isSuperAdminSelected && cleanedPayload.rolesAtLocation.length > 0) {
          this.whiteListStaff({
            personId: _successLog.successList[0].personKey,
            locations: cleanedPayload.rolesAtLocation.map(loc => ({
              locationId: loc.locationId,
              status: true // Newly created staff should be active at all assigned locations
            }))
          });
        }
        setStaffDetails({});
      },
      _errorLog => {
        setStaffDetails({});
        React.setGlobal({
          isLoading: false
        });
        // Show error in response dialog
        const responseData = [{
          statusCode: _errorLog.status || 500,
          text: _errorLog.statusMessage || 'Something went wrong while creating user.'
        }];
        
        this.setState({
          manageStaff: {
            ...this.state.manageStaff,
            isOpen: false,
            isConfirmationOpen: false,
            data: null
          },
          response: {
            isOpen: true,
            data: responseData
          }
        });
      }
    );
  };

  render() {
    const { classes, unleashProps } = this.props;
    const { showEmptyDataSourceMessage } = this.state;
    const locationAssignment =
      isPermitted(this.global.cbacConfigFlag, this.global.permissions, 'Assign_Admin_To_Different_Location') ||
      isPermitted(this.global.cbacConfigFlag, this.global.permissions, 'Add_Admin');
    
    // Early return for full-screen CreateStaff component when feature flag is enabled
    const shouldShowCreateStaff = unleashProps && unleashProps.isFlagEnabled() && this.state.manageStaff.isOpen && !this.state.manageStaff.isEdit;
    if (shouldShowCreateStaff) {
      return (
        <Suspense fallback={<Loader isLoading={true} />}>
          <Box style={{ position: 'relative', width: '100%'}}>
            <CreateStaff
              onClose={() => this.handleManageDialog(false)}
              onCreateUser={(formValues) => {
                this.handleCreateStaffFromForm(formValues);
              }}
              isCreateLoading={this.global.isLoading}
              activeLocation={this.state.location.active}
              locationType={this.state.location.type}
              jurisdictionName={this.getJurisdictionName()}
              isSuperAdmin={this.global.isSuperAdmin}
              isSystemAdmin={this.global.isSystemAdmin}
              canManageCrossUserPermissionOverrides={Boolean(
                this.global.isSuperAdmin || this.global.isSystemAdmin
              )}
              createStaffV2Enabled={Boolean(unleashProps.isFlagEnabled())}
            />
          </Box>
        </Suspense>
      );
    }

    return (
      <Suspense fallback={<Loader isLoading={true} />}>
        <Loader isLoading={this.global.isLoading} />
        <div style={{ padding: '1.5rem', width: '100%' }}>
          <Grid container spacing={3}>
            <Grid container item justify="space-between">
              <Box display="flex" flexDirection="row" justifyContent="flex-end" width="100%">
                <Paper
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 60,
                    margin: 0,
                    marginBotton: '-38px',
                    marginRight: '10px',
                    height: 36
                  }}
                >
                  <Tooltip title="refresh staff">
                    <IconButton color="primary" size="small" onClick={() => this.getStaffList(this.props.location.active)}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Paper>
                <Box display="flex" flexDirection="row">
                  {this.state.location && this.state.location.type !== 'hu' && (
                    <PermissionControl permissionName="Add_Admin">
                      <Box>
                        <Button
                          id="add-form"
                          data-cy-name="add-staff"
                          onClick={() => {
                            this.getStates();
                            this.handleManageDialog(true);
                          }}
                          variant="contained"
                          color="primary"
                          disableElevation
                        >
                          {unleashProps && unleashProps.isFlagEnabled() ? 'Create Admin User' : 'Add Staff'}
                        </Button>
                      </Box>
                    </PermissionControl>
                  )}
                  <Box marginLeft="10px">
                    <Button onClick={() => this.selectData('all')} variant="contained" color="primary" disableElevation>
                      Select All
                    </Button>
                  </Box>
                  <Box marginLeft="10px">
                    <Button onClick={() => this.selectData('page')} variant="contained" color="primary" disableElevation>
                      Select This Page
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Grid>
            {this.state.location && (!unleashProps || !unleashProps.isFlagEnabled()) && (
              <ManageStaffContainer
                {...this.state.manageStaff}
                isAttorneyAllowed={this.state.isAttorneyAllowed}
                isAutoCreateAvailable={this.state.isAutoCreateAvailable}
                activeLocation={this.state.location.active}
                handleManageDialog={this.handleManageDialog}
                formDialogData={JSON.stringify(this.state.manageStaff.data)}
                isPermissionAllowed={locationAssignment}
                AssignedToDiffLocation={true}
                UnassignedToDiffLocation={true}
                locationType={this.state.location.type}
                refreshStaffList={opts => this.getStaffList(this.props.location.active, opts)}
              />
            )}

            <ConfirmationModal
              isOpen={this.state.manageStaff.isConfirmationOpen}
              onClose={() => this.handleConfirmationDialog(false)}
              onContinue={() => this.manageStaffForm(this.state.manageStaff.data)}
              heading={`${this.state.manageStaff.isEdit ? 'Update' : 'Create new'} staff account?`}
              description={this.state.manageStaff.description}
            />
            <ResponseBox
              heading="Staff Account Confirmation"
              isOpen={this.state.response.isOpen}
              onClose={() => this.handleResponseDialog(false)}
              data={this.state.response.data}
              groupedView={false}
            />
            <Grid container item className={classes.datatable}>
              <Datatable
                style={{ width: '100%' }}
                tableRef={ref => (this.tableRef = ref)}
                columns={this.getStaffListColumns()}
                data={this.state.staff.data}
                components={{
                  Toolbar: props => (
                    <div>
                      <MTableToolbar {...props} />
                    </div>
                  )
                }}
                options={{
                  showEmptyDataSourceMessage: showEmptyDataSourceMessage,
                  exportAllData: true,
                  sorting: true,
                  pageSize: 20,
                  selection: false,
                  showSelectAllCheckbox: true,
                  filtering: true,
                  exportButton: true,
                  Sort: SortIcon,
                  SortArrow: ArrowDownwardIcon,
                  actionsColumnIndex: 0,
                  search: false,
                  exportFileName: 'Staff-List',
                  selectionProps: rowData => ({
                    id: `checkbox-${rowData.formId}`
                  })
                }}
                icons={{
                  Search: SearchIcon,
                  ResetSearch: CloseIcon,
                  FirstPage: FirstPageIcon,
                  LastPage: LastPageIcon,
                  NextPage: NavigateNextIcon,
                  PreviousPage: NavigateBeforeIcon,
                  Filter: FilterListIcon,
                  SortArrow: ArrowUpwardIcon,
                  Export: forwardRef((props, ref) => <SaveAltIcon data-cy-name="save-btn" {...props} ref={ref} />),
                  Edit: EditIcon,
                  User: UserIcon
                }}
                title={
                  <Box>
                    <Typography component="p" className={classes.locationName}>
                      {this.state.location &&
                      this.state.location.collection.find(location => location.locationId === this.state.location.active)
                        ? this.state.location.collection.find(location => location.locationId === this.state.location.active)
                            .locationName
                        : ''}
                    </Typography>
                    <Typography component="p" className={classes.tabName}>
                      Staff
                    </Typography>
                  </Box>
                }
              />
            </Grid>
          </Grid>
        </div>
      </Suspense>
    );
  }
}

export default withStyles(styles)(withUnleash(Staff, UnleashService.FLAGS.CREATE_STAFF_V2));
